/**
 * LineageTimeline — shared horizontal timeline renderer.
 *
 * Stateless: takes a typed `events` array and renders the rail, dots, labels,
 * and detail panel. Supports stream filters (All / Identity / Operations) and
 * Fit vs Scroll layout modes.
 */

import { useState, useRef, useMemo, useEffect } from 'react'
import { Link } from 'react-router-dom'
import {
  Plus, Scissors, GitMerge, Link2, Users, Star, Clock,
  Activity, CheckCircle, Shield, Archive, Calendar,
  GitBranch, ChevronRight, ExternalLink, Info, Layers,
  Box, MessageSquare, Video, FileText, FileSearch,
  RefreshCw, Flag, Cpu, BarChart2, Lock, Unlock, GitCommit, Unlink,
  Zap, ArrowUpRight, User, CornerDownRight, TrendingUp,
  FileEdit,
} from 'lucide-react'

// ─── Event types ─────────────────────────────────────────────────────────────

export type LineageEventType =
  // Item-origin types (kept for cross-use in item timeline wrapper)
  | 'created'
  | 'shaping_started'
  | 'shaping_completed'
  | 'leadership_approved'
  | 'promoted'
  | 'archived'
  | 'deferred'
  | 'reactivated'
  | 'package_formed'
  | 'cluster_formed'
  | 'dependency_added'
  | 'prerequisite_added'
  | 'aggregation_added'
  | 'elaboration_added'
  | 'breakdown_added'
  | 'contains_added'
  | 'session_added'
  | 'cycle_added'
  | 'cycle_status_changed'
  | 'cycle_carryover'
  | 'lineage_split'
  | 'lineage_merge'
  | 'lineage_supersede'
  | 'lineage_fork'
  | 'lineage_retire'
  // Cycle-origin types
  | 'cycle_created'
  | 'cycle_template_applied'
  | 'cycle_phase_transition'
  | 'cycle_track_added'
  | 'cycle_session_run'
  | 'cycle_output_published'
  // Track — origin types
  | 'track_created'
  | 'track_official_session'
  | 'track_value_source_selected'
  | 'track_ranking_locked'
  // Session-origin types
  | 'session_created'
  | 'session_tool_selected'
  | 'session_run_started'
  | 'session_run_completed'
  | 'session_results_published'
  | 'session_catchball_seeded'
  // Relationship dissolution
  | 'relationship_dissolved'
  // Artifact types
  | 'artifact_transcript'
  | 'artifact_video'
  | 'artifact_document'
  // Rescoring events
  | 'score_revised'
  // Item audit events (see activity/itemEventMapping.ts)
  | 'imported'
  | 'completed'
  | 'classification_changed'
  | 'parent_changed'
  | 'dedupe_reviewed'
  | 'pool_added'
  | 'relationship_added'
  | 'decision_recorded'
  | 'item_changed'

/**
 * Stream classification:
 *  'lineage'  — structural identity transforms (split, merge, supersede, fork, retire).
 *               These correspond directly to lineage_events table rows.
 *  'history'  — operational events: what happened to or around this item over time.
 *               These correspond to audit_events, governance_events, and domain facts
 *               (cycle participation, session membership, relationships, promotions).
 */
export type EventStream = 'lineage' | 'history'

export interface TimelineEvent {
  id: string
  type: LineageEventType
  timestamp: Date
  label: string
  sublabel?: string
  detail?: string
  actor?: string
  linkedId?: string
  linkedLabel?: string
  linkedPath?: string
  /** True when the timestamp is estimated rather than exact. */
  isEstimated?: boolean
  /** Resolved actor display name (pass in from hook via useActorDisplayMap). */
  actorDisplay?: string
  /**
   * Stream this event belongs to.
   * 'lineage' = structural identity transform (split/merge/supersede/fork/retire).
   * 'history' = operational event (everything else).
   * Defaults to 'history' when absent.
   */
  stream?: EventStream
}

// ─── Visual config per event type ─────────────────────────────────────────────

const EVENT_CONFIG: Record<
  LineageEventType,
  {
    icon: React.ComponentType<{ className?: string }>
    dot: string
    ring: string
    badge: string
    label: string
  }
> = {
  // Item events (kept for item timeline wrapper compatibility)
  created:                  { icon: Plus,         dot: 'bg-[var(--surface)]',    ring: 'ring-slate-200',    badge: 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)]/40',     label: 'Created' },
  shaping_started:          { icon: Activity,     dot: 'bg-[var(--slate)]',      ring: 'ring-sky-200',      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40',            label: 'Work Started' },
  shaping_completed:        { icon: CheckCircle,  dot: 'bg-[var(--slate)]',      ring: 'ring-sky-200',      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40',            label: 'Item Ready' },
  leadership_approved:      { icon: Shield,       dot: 'bg-[var(--mustard)]',    ring: 'ring-amber-200',    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]',    label: 'Approved' },
  promoted:                 { icon: Star,         dot: 'bg-[var(--mustard)]',    ring: 'ring-amber-300',    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]',    label: 'Promoted' },
  archived:                 { icon: Archive,      dot: 'bg-[var(--surface)]',     ring: 'ring-gray-200',     badge: 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)]/40',        label: 'Archived' },
  deferred:                 { icon: Clock,        dot: 'bg-[var(--mustard)]',   ring: 'ring-yellow-200',   badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]', label: 'Deferred' },
  reactivated:              { icon: RefreshCw,    dot: 'bg-[var(--green)]',     ring: 'ring-emerald-200',  badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]', label: 'Brought back to active' },
  package_formed:           { icon: Link2,        dot: 'bg-[var(--mustard)]',   ring: 'ring-orange-200',   badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]', label: 'Package' },
  cluster_formed:           { icon: Layers,       dot: 'bg-cyan-500',     ring: 'ring-cyan-200',     badge: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/40 dark:text-cyan-300',        label: 'Cluster' },
  dependency_added:         { icon: GitBranch,    dot: 'bg-[var(--slate)]',     ring: 'ring-blue-200',     badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40',        label: 'Dependency' },
  prerequisite_added:       { icon: GitBranch,    dot: 'bg-[var(--slate)]',      ring: 'ring-sky-200',      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40',            label: 'Prerequisite' },
  aggregation_added:        { icon: Box,          dot: 'bg-[var(--green)]',  ring: 'ring-emerald-200',  badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]', label: 'Aggregation' },
  elaboration_added:        { icon: MessageSquare, dot: 'bg-[var(--slate)]',  ring: 'ring-violet-200',   badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40', label: 'Elaboration' },
  breakdown_added:          { icon: Box,          dot: 'bg-[var(--green)]',     ring: 'ring-teal-200',     badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]',        label: 'Breakdown' },
  contains_added:           { icon: CornerDownRight, dot: 'bg-cyan-500', ring: 'ring-cyan-200',     badge: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',        label: 'Contains' },
  session_added:            { icon: Users,        dot: 'bg-[var(--slate)]',   ring: 'ring-purple-200',   badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40', label: 'Session' },
  cycle_added:              { icon: Calendar,     dot: 'bg-[var(--coral)]',     ring: 'ring-rose-200',     badge: 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/40 dark:text-[var(--coral)]',        label: 'Cycle' },
  cycle_status_changed:     { icon: RefreshCw,    dot: 'bg-[var(--coral)]',     ring: 'ring-rose-200',     badge: 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/40 dark:text-[var(--coral)]',        label: 'Cycle Status' },
  cycle_carryover:          { icon: ArrowUpRight, dot: 'bg-[var(--mustard)]',   ring: 'ring-orange-200',   badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]', label: 'Carried Over' },
  lineage_split:            { icon: Scissors,     dot: 'bg-[var(--green)]',     ring: 'ring-teal-200',     badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]',        label: 'Split' },
  lineage_merge:            { icon: GitMerge,     dot: 'bg-[var(--green)]',     ring: 'ring-teal-200',     badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]',        label: 'Merged' },
  lineage_supersede:        { icon: ArrowUpRight, dot: 'bg-[var(--slate)]',   ring: 'ring-violet-200',   badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40', label: 'Superseded' },
  lineage_fork:             { icon: GitBranch,    dot: 'bg-cyan-600',     ring: 'ring-cyan-200',     badge: 'bg-cyan-100 text-cyan-800 dark:bg-cyan-900/40 dark:text-cyan-300',        label: 'Forked' },
  lineage_retire:           { icon: Archive,      dot: 'bg-[var(--surface)]',     ring: 'ring-gray-200',     badge: 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)]/40',        label: 'Retired' },
  // Cycle events
  cycle_created:            { icon: Plus,         dot: 'bg-[var(--surface)]',    ring: 'ring-slate-200',    badge: 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)]/40',     label: 'Created' },
  cycle_template_applied:   { icon: GitCommit,    dot: 'bg-[var(--slate)]',   ring: 'ring-indigo-200',   badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40', label: 'Template Applied' },
  cycle_phase_transition:   { icon: RefreshCw,    dot: 'bg-[var(--slate)]',   ring: 'ring-violet-200',   badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40', label: 'Phase' },
  cycle_track_added:        { icon: Flag,         dot: 'bg-[var(--slate)]',     ring: 'ring-blue-200',     badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40',        label: 'Track Added' },
  cycle_session_run:        { icon: Users,        dot: 'bg-[var(--slate)]',   ring: 'ring-purple-200',   badge: 'bg-[var(--slate)] text-white dark:bg-[var(--slate)]/30 dark:text-[var(--text-ter)]',      label: 'Session Run' },
  cycle_output_published:   { icon: ArrowUpRight, dot: 'bg-[var(--green)]',    ring: 'ring-green-200',    badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]',    label: 'Output Published' },
  // Track events
  track_created:            { icon: Plus,         dot: 'bg-[var(--surface)]',    ring: 'ring-slate-200',    badge: 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)]/40',     label: 'Track Created' },
  track_official_session:   { icon: Star,         dot: 'bg-[var(--mustard)]',    ring: 'ring-amber-300',    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]',    label: 'Published Track Result' },
  track_value_source_selected: { icon: Zap,       dot: 'bg-[var(--mustard)]',   ring: 'ring-yellow-200',   badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]', label: 'Value Source' },
  track_ranking_locked:     { icon: Lock,         dot: 'bg-[var(--surface)]',     ring: 'ring-gray-300',     badge: 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)]/40',        label: 'Ranking Locked' },
  // Session events
  session_created:          { icon: Plus,         dot: 'bg-[var(--surface)]',    ring: 'ring-slate-200',    badge: 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)]/40',     label: 'Created' },
  session_tool_selected:    { icon: Cpu,          dot: 'bg-[var(--slate)]',     ring: 'ring-blue-200',     badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40',        label: 'Tool Selected' },
  session_run_started:      { icon: Activity,     dot: 'bg-[var(--slate)]',      ring: 'ring-sky-200',      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40',            label: 'Run Started' },
  session_run_completed:    { icon: CheckCircle,  dot: 'bg-[var(--green)]',    ring: 'ring-green-200',    badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]',    label: 'Run Completed' },
  session_results_published:{ icon: BarChart2,    dot: 'bg-[var(--green)]',  ring: 'ring-emerald-200',  badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]', label: 'Results Published' },
  session_catchball_seeded: { icon: GitBranch,    dot: 'bg-[var(--mustard)]',   ring: 'ring-orange-200',   badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]', label: 'Catchball Seeded' },
  // Artifact events
  artifact_transcript:      { icon: FileText,     dot: 'bg-[var(--slate)]',      ring: 'ring-sky-200',      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40',            label: 'AI Transcript' },
  artifact_video:           { icon: Video,        dot: 'bg-[var(--coral)]',     ring: 'ring-rose-200',     badge: 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/40 dark:text-[var(--coral)]',        label: 'Video' },
  artifact_document:        { icon: FileSearch,   dot: 'bg-[var(--mustard)]',    ring: 'ring-amber-300',    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]',    label: 'Document' },
  // Relationship dissolution
  relationship_dissolved:   { icon: Unlink,       dot: 'bg-[var(--coral)]',      ring: 'ring-red-200',      badge: 'bg-[var(--coral-dim)] text-[var(--coral)] dark:bg-[var(--coral)]/40 dark:text-[var(--coral)]',            label: 'Dissolved' },
  // Rescoring events
  imported:                 { icon: Plus,         dot: 'bg-[var(--surface)]',    ring: 'ring-slate-200',    badge: 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)]/40',     label: 'Imported' },
  completed:                { icon: CheckCircle,  dot: 'bg-[var(--green)]',      ring: 'ring-emerald-200',  badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]',          label: 'Completed' },
  classification_changed:   { icon: Layers,       dot: 'bg-[var(--slate)]',      ring: 'ring-sky-200',      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40',          label: 'Classification' },
  parent_changed:           { icon: CornerDownRight, dot: 'bg-[var(--slate)]',   ring: 'ring-sky-200',      badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40',          label: 'Parent' },
  dedupe_reviewed:          { icon: FileSearch,   dot: 'bg-[var(--surface)]',    ring: 'ring-gray-200',     badge: 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)]/40',     label: 'Duplicate Review' },
  pool_added:               { icon: Box,          dot: 'bg-[var(--mustard)]',    ring: 'ring-amber-200',    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]',  label: 'Added to Pool' },
  item_changed:             { icon: FileEdit,     dot: 'bg-[var(--surface)]',    ring: 'ring-slate-200',    badge: 'bg-[var(--hover-tint)] text-[var(--text-sec)] dark:bg-[var(--bg-raised)]/40',     label: 'Changed' },
  relationship_added:       { icon: Link2,        dot: 'bg-[var(--slate)]',      ring: 'ring-blue-200',     badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40',          label: 'Relationship' },
  decision_recorded:        { icon: Flag,         dot: 'bg-[var(--mustard)]',    ring: 'ring-amber-300',    badge: 'bg-[var(--mustard-dim)] text-[var(--mustard)] dark:bg-[var(--mustard)]/40 dark:text-[var(--mustard)]',  label: 'Decision' },
  score_revised:            { icon: TrendingUp,   dot: 'bg-[var(--slate)]',   ring: 'ring-violet-200',   badge: 'bg-[var(--slate-dim)] text-[var(--text-sec)] dark:bg-[var(--slate)]/40', label: 'Score Revised' },
  // Misc
  session_run_completed_alias: { icon: Unlock,    dot: 'bg-[var(--green)]',     ring: 'ring-teal-200',     badge: 'bg-[var(--green-dim)] text-[var(--green)] dark:bg-[var(--green)]/40 dark:text-[var(--text-ter)]',        label: 'Unlocked' },
} as Record<LineageEventType, (typeof EVENT_CONFIG)[keyof typeof EVENT_CONFIG]>

// ─── Date helpers ─────────────────────────────────────────────────────────────

// eslint-disable-next-line react-refresh/only-export-components
export function toTimelineDate(val: unknown): Date | null {
  if (!val) return null
  if (val instanceof Date) return isNaN(val.getTime()) ? null : val
  const d = new Date(val as string)
  return isNaN(d.getTime()) ? null : d
}

function formatDate(d: Date) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function formatDateShort(d: Date) {
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
}

// ─── Layout calculation ───────────────────────────────────────────────────────

const MIN_GAP_PX = 110
const MIN_GAP_FIT_PX = 52
const SIDE_PAD_PX = 56

type StreamFilter = 'all' | 'lineage' | 'history'
type LayoutMode = 'scroll' | 'fit'

const STREAM_FILTER_OPTIONS: Array<{ id: StreamFilter; label: string }> = [
  { id: 'all', label: 'All' },
  { id: 'lineage', label: 'Identity' },
  { id: 'history', label: 'Operations' },
]

function computePositions(events: TimelineEvent[], fitWidth?: number): number[] {
  if (events.length === 0) return []
  if (events.length === 1) {
    if (fitWidth && fitWidth > 0) return [fitWidth / 2]
    return [SIDE_PAD_PX]
  }

  const min = events[0].timestamp.getTime()
  const max = events[events.length - 1].timestamp.getTime()
  const span = max - min || 1

  if (fitWidth && fitWidth > SIDE_PAD_PX * 2) {
    const inner = fitWidth - SIDE_PAD_PX * 2
    const minGap = Math.min(MIN_GAP_FIT_PX, inner / (events.length - 1))
    const positions = events.map(
      (e) => SIDE_PAD_PX + ((e.timestamp.getTime() - min) / span) * inner,
    )
    for (let i = 1; i < positions.length; i++) {
      if (positions[i] - positions[i - 1] < minGap) {
        positions[i] = positions[i - 1] + minGap
      }
    }
    const overflow = positions[positions.length - 1] + SIDE_PAD_PX - fitWidth
    if (overflow > 0) {
      const shift = overflow
      for (let i = 0; i < positions.length; i++) {
        positions[i] = Math.max(SIDE_PAD_PX, positions[i] - shift)
      }
    }
    return positions
  }

  const minCanvas = SIDE_PAD_PX * 2 + (events.length - 1) * MIN_GAP_PX
  const positions = events.map(
    (e) => SIDE_PAD_PX + ((e.timestamp.getTime() - min) / span) * (minCanvas - SIDE_PAD_PX * 2),
  )

  for (let i = 1; i < positions.length; i++) {
    if (positions[i] - positions[i - 1] < MIN_GAP_PX) {
      positions[i] = positions[i - 1] + MIN_GAP_PX
    }
  }

  return positions
}

// ─── Component ────────────────────────────────────────────────────────────────

export interface LineageTimelineProps {
  events: TimelineEvent[]
  title?: string
  onEventCount?: (count: number) => void
  className?: string
  /** When true, show a stable shell while async lineage data loads. */
  isLoading?: boolean
  /**
   * When set, only events matching the given stream are shown.
   * Omit to use the in-header stream filter (when both streams are present).
   */
  streamFilter?: StreamFilter
  /** Show All / Identity / Operations pills. Default: auto when both streams exist. */
  showStreamFilter?: boolean
  /** Optional node rendered on the right side of the header row (e.g. a journey link). */
  headerAction?: React.ReactNode
}

export function LineageTimeline({
  events: allEvents,
  title = 'Item Timeline',
  onEventCount,
  className = '',
  isLoading = false,
  streamFilter: streamFilterProp,
  showStreamFilter,
  headerAction,
}: LineageTimelineProps) {
  const [internalStreamFilter, setInternalStreamFilter] = useState<StreamFilter>('all')
  const [layoutMode, setLayoutMode] = useState<LayoutMode>('scroll')
  const [containerWidth, setContainerWidth] = useState(0)

  const hasLineageStream = useMemo(
    () => allEvents.some((e) => (e.stream ?? 'history') === 'lineage'),
    [allEvents],
  )
  const hasHistoryStream = useMemo(
    () => allEvents.some((e) => (e.stream ?? 'history') === 'history'),
    [allEvents],
  )
  const streamFilterEnabled =
    showStreamFilter ?? (hasLineageStream && hasHistoryStream)
  const activeStreamFilter = streamFilterProp ?? internalStreamFilter

  const events = useMemo(
    () =>
      activeStreamFilter === 'all'
        ? allEvents
        : allEvents.filter((e) => (e.stream ?? 'history') === activeStreamFilter),
    [allEvents, activeStreamFilter],
  )
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = scrollRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const observer = new ResizeObserver(([entry]) => {
      setContainerWidth(entry.contentRect.width)
    })
    observer.observe(el)
    setContainerWidth(el.clientWidth)
    return () => observer.disconnect()
  }, [isLoading, allEvents.length])

  useEffect(() => {
    if (selectedId && !events.some((e) => e.id === selectedId)) {
      setSelectedId(null)
    }
  }, [events, selectedId])

  const fitWidth = layoutMode === 'fit' && containerWidth > 0 ? containerWidth : undefined
  const positions = useMemo(() => computePositions(events, fitWidth), [events, fitWidth])
  const scrollCanvasWidth = positions.length ? positions[positions.length - 1] + SIDE_PAD_PX : 0
  const canvasWidth =
    layoutMode === 'fit' && containerWidth > 0
      ? containerWidth
      : scrollCanvasWidth
  const selectedEvent = events.find((e) => e.id === selectedId)
  const presentTypes = useMemo(() => [...new Set(events.map((e) => e.type))], [events])

  // Notify parent of event count changes
  useMemo(() => {
    onEventCount?.(events.length)
  }, [events.length, onEventCount])

  const RAIL_Y = 58

  const getConfig = (type: LineageEventType) =>
    EVENT_CONFIG[type] ?? EVENT_CONFIG['created']

  if (isLoading) {
    return (
      <div
        className={` border border-[var(--rule)] dark:border-[var(--rule,#2a2f3a)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised,#1a1d24)] p-3 ${className}`}
        style={{ minHeight: 200 }}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Activity className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)] opacity-40" />
            <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri,#e8eaf0)]">{title}</h3>
          </div>
        </div>
        <div className="flex flex-wrap gap-1 mb-3">
          <span className="inline-block h-4 w-16 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface,#22262f)] animate-pulse" />
          <span className="inline-block h-4 w-12 rounded-full bg-[var(--hover-tint)] dark:bg-[var(--surface,#22262f)] animate-pulse" />
        </div>
        <div className="flex items-center justify-center border border-dashed border-[var(--rule)] dark:border-[var(--rule,#2a2f3a)] bg-[var(--surface)]/50 dark:bg-[var(--surface,#22262f)]/30"
          style={{ height: 120 }}
        >
          <div className="text-center text-[var(--text-ter)] dark:text-[var(--text-ter,#7a8094)]">
            <Activity className="h-6 w-6 mx-auto mb-1.5 opacity-30 animate-pulse" />
            <p className="text-xs">Loading timeline…</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={` border border-[var(--rule)] dark:border-[var(--rule,#2a2f3a)] bg-[var(--bg-raised)] dark:bg-[var(--bg-raised,#1a1d24)] p-3 ${className}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-[var(--text-ter)] dark:text-[var(--text-ter)]" />
          <h3 className="text-sm font-semibold text-[var(--text-pri)] dark:text-[var(--text-pri,#e8eaf0)]">{title}</h3>
          {events.length > 0 && (
            <span className="text-xs bg-[var(--hover-tint)] dark:bg-[var(--surface,#22262f)] text-[var(--text-sec)] dark:text-[var(--text-ter,#7a8094)] rounded-full px-2 py-0.5 font-medium">
              {events.length} event{events.length !== 1 ? 's' : ''}
            </span>
          )}
        </div>
        {headerAction}
      </div>

      {/* Stream + layout controls */}
      {(streamFilterEnabled || allEvents.length > 1) && (
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          {streamFilterEnabled && streamFilterProp == null && (
            <div
              className="inline-flex border border-[var(--rule)] dark:border-[var(--rule,#2a2f3a)] p-0.5 bg-[var(--surface)]/80 dark:bg-[var(--surface,#22262f)]/60"
              role="group"
              aria-label="Filter timeline by stream"
            >
              {STREAM_FILTER_OPTIONS.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => setInternalStreamFilter(id)}
                  className={`px-2.5 py-1 text-xs font-medium  transition-colors ${
                    activeStreamFilter === id
                      ? 'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised,#1a1d24)] text-[var(--text-ter)] dark:text-[var(--text-ter)] shadow-sm'
                      : 'text-[var(--text-ter)] dark:text-[var(--text-ter,#7a8094)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri,#e8eaf0)]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          )}

          {allEvents.length > 1 && (
            <div
              className="inline-flex border border-[var(--rule)] dark:border-[var(--rule,#2a2f3a)] p-0.5 bg-[var(--surface)]/80 dark:bg-[var(--surface,#22262f)]/60 ml-auto"
              role="group"
              aria-label="Timeline width mode"
            >
              <button
                type="button"
                onClick={() => setLayoutMode('fit')}
                className={`px-2.5 py-1 text-xs font-medium  transition-colors ${
                  layoutMode === 'fit'
                    ? 'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised,#1a1d24)] text-[var(--text-ter)] dark:text-[var(--text-ter)] shadow-sm'
                    : 'text-[var(--text-ter)] dark:text-[var(--text-ter,#7a8094)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri,#e8eaf0)]'
                }`}
              >
                Fit
              </button>
              <button
                type="button"
                onClick={() => setLayoutMode('scroll')}
                className={`px-2.5 py-1 text-xs font-medium  transition-colors ${
                  layoutMode === 'scroll'
                    ? 'bg-[var(--bg-raised)] dark:bg-[var(--bg-raised,#1a1d24)] text-[var(--text-ter)] dark:text-[var(--text-ter)] shadow-sm'
                    : 'text-[var(--text-ter)] dark:text-[var(--text-ter,#7a8094)] hover:text-[var(--text-pri)] dark:hover:text-[var(--text-pri,#e8eaf0)]'
                }`}
              >
                Scroll
              </button>
            </div>
          )}
        </div>
      )}

      {/* Legend */}
      {presentTypes.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-3">
          {presentTypes.map((type) => {
            const cfg = getConfig(type)
            return (
              <span
                key={type}
                className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full font-medium border border-transparent ${cfg.badge}`}
              >
                <cfg.icon className="h-2.5 w-2.5" />
                {cfg.label}
              </span>
            )
          })}
        </div>
      )}

      {/* Timeline canvas */}
      {events.length > 0 && (
        <div
          ref={scrollRef}
          className={`overflow-y-visible pb-2 -mx-1 px-1 ${
            layoutMode === 'fit' ? 'overflow-x-hidden' : 'overflow-x-auto'
          }`}
          style={{ WebkitOverflowScrolling: 'touch' }}
        >
          <div
            className="relative select-none"
            style={{
              width: layoutMode === 'fit' ? '100%' : canvasWidth > 0 ? `max(100%, ${canvasWidth}px)` : '100%',
              height: 130,
            }}
          >
            {/* Rail */}
            <div
              className="absolute bg-[var(--hover-tint)] dark:bg-[var(--rule,#2a2f3a)] rounded-full"
              style={{
                top: RAIL_Y - 1,
                left: positions[0],
                width: positions[positions.length - 1] - positions[0],
                height: 2,
              }}
            />

            {/* Stage gradient overlay */}
            <div
              className="absolute rounded-full opacity-30 pointer-events-none"
              style={{
                top: RAIL_Y - 1,
                left: positions[0],
                width: positions[positions.length - 1] - positions[0],
                height: 2,
                background: 'linear-gradient(to right, #94a3b8, #38bdf8, #a855f7, #10b981)',
              }}
            />

            {events.map((event, idx) => {
              const x = positions[idx]
              const cfg = getConfig(event.type)
              const isSelected = event.id === selectedId
              const above = idx % 2 === 0

              return (
                <div key={event.id}>
                  {/* Tick */}
                  <div
                    className="absolute w-px bg-[var(--hover-tint)]"
                    style={{
                      left: x - 0.5,
                      top: above ? RAIL_Y - 36 : RAIL_Y + 20,
                      height: 16,
                    }}
                  />

                  {/* Dot */}
                  <button
                    onClick={() => setSelectedId(isSelected ? null : event.id)}
                    className={`absolute flex items-center justify-center rounded-full transition-all duration-150
                      ${cfg.dot} ring-2 ${cfg.ring}
                      ${isSelected ? 'scale-125 shadow-lg z-10' : 'hover:scale-110 hover:z-10'}
                    `}
                    style={{ left: x - 14, top: RAIL_Y - 14, width: 28, height: 28 }}
                    title={event.label}
                  >
                    <cfg.icon className="h-3.5 w-3.5 text-white" />
                  </button>

                  {/* Label */}
                  <div
                    className={`absolute text-center pointer-events-none transition-opacity
                      ${isSelected ? 'opacity-100' : 'opacity-70'}`}
                    style={{
                      left: x - 44,
                      width: 88,
                      top: above ? RAIL_Y - 76 : RAIL_Y + 36,
                    }}
                  >
                    <div
                      className={`text-xs font-medium leading-tight truncate ${
                        isSelected ? 'text-[var(--text-pri)] dark:text-[var(--text-pri,#e8eaf0)]' : 'text-[var(--text-sec)] dark:text-[var(--text-sec,#b0b8cc)]'
                      }`}
                    >
                      {event.label}
                    </div>
                    <div className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-dim,#636880)] mt-0.5">
                      {formatDateShort(event.timestamp)}
                      {event.isEstimated && (
                        <span className="ml-0.5 text-[var(--text-ter)]" title="Timestamp estimated">~</span>
                      )}
                    </div>
                    {event.actorDisplay && (
                      <div className="flex items-center justify-center gap-0.5 mt-0.5">
                        <User className="h-2.5 w-2.5 text-[var(--text-ter)] dark:text-[var(--text-dim,#636880)] flex-shrink-0" />
                        <span className="text-[10px] text-[var(--text-ter)] dark:text-[var(--text-dim,#636880)] truncate">
                          {event.actorDisplay}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Selected event detail panel */}
      {selectedEvent && (
        <div className="mt-2 border border-[var(--slate)] bg-[var(--slate-dim)] px-3 py-2 flex items-start gap-2.5 animate-in fade-in slide-in-from-bottom-1 duration-150">
          {(() => {
            const cfg = getConfig(selectedEvent.type)
            return (
              <div
                className={`flex-shrink-0 flex items-center justify-center rounded-full ${cfg.dot} mt-0.5`}
                style={{ width: 32, height: 32 }}
              >
                <cfg.icon className="h-4 w-4 text-white" />
              </div>
            )
          })()}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <span className="font-semibold text-[var(--text-pri)] text-sm">{selectedEvent.label}</span>
              {selectedEvent.sublabel && (
                <span className="text-xs text-[var(--text-ter)] bg-[var(--bg-raised)] rounded px-1.5 py-0.5 border border-[var(--rule)]">
                  {selectedEvent.sublabel}
                </span>
              )}
              {selectedEvent.isEstimated && (
                <span className="text-xs text-[var(--text-sec)] flex items-center gap-1">
                  <Info className="h-3 w-3" /> estimated date
                </span>
              )}
            </div>
            <div className="text-xs text-[var(--text-sec)] mt-0.5">
              {formatDate(selectedEvent.timestamp)}
              {(selectedEvent.actorDisplay ?? selectedEvent.actor) && (
                <span className="ml-2 text-[var(--text-sec)]">
                  · {selectedEvent.actorDisplay ?? selectedEvent.actor}
                </span>
              )}
            </div>
            {selectedEvent.detail && (
              <div className="text-sm text-[var(--text-sec)] mt-1">{selectedEvent.detail}</div>
            )}
            {selectedEvent.linkedLabel && selectedEvent.linkedPath && (
              <Link
                to={selectedEvent.linkedPath}
                className="mt-1.5 inline-flex items-center gap-1 text-xs text-[var(--text-sec)] font-medium hover:text-[var(--text-pri)] hover:underline transition-colors"
              >
                <ExternalLink className="h-3 w-3" />
                {selectedEvent.linkedLabel}
                <ChevronRight className="h-3 w-3 opacity-50" />
              </Link>
            )}
          </div>
          <button
            onClick={() => setSelectedId(null)}
            className="flex-shrink-0 text-[var(--text-sec)] hover:text-[var(--text-sec)] text-xs mt-0.5"
          >
            ✕
          </button>
        </div>
      )}

      {/* Empty state */}
      {events.length === 0 && (
        <div className="flex items-center justify-center py-10 text-[var(--text-ter)] dark:text-[var(--text-ter,#7a8094)]">
          <div className="text-center">
            <Activity className="h-8 w-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">
              {allEvents.length === 0
                ? 'No provenance events recorded yet'
                : activeStreamFilter === 'lineage'
                  ? 'No identity events in this stream'
                  : activeStreamFilter === 'history'
                    ? 'No operational events in this stream'
                    : 'No events match this filter'}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}
